import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { vendorService, Contract } from '../services/vendor.service';

// Static color configurations - defined outside component to avoid recreation
const STATUS_COLORS = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
} as const;

// Animation variants - defined outside component to avoid recreation
const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

function getNoticeDeadlineColor(noticeDeadline: string): string {
  const now = new Date();
  const deadline = new Date(noticeDeadline);
  const diffMs = deadline.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 0) {return 'text-danger-600 dark:text-danger-400';}
  if (diffDays <= 30) {return 'text-warning-600 dark:text-warning-400';}
  return 'text-secondary-600 dark:text-secondary-400';
}

function ContractCard({ contract }: { contract: Contract }) {
  const statusConfig = vendorService.getContractStatusConfig(contract.status);
  const typeLabel = vendorService.getContractTypeLabel(contract.contractType);

  return (
    <Link to={`/legal/contracts/${contract.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                  {contract.title}
                </h3>
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {contract.contractNumber}
                </span>
              </div>
              {contract.vendor && (
                <p className="text-secondary-600 dark:text-secondary-400 text-sm mb-3">
                  {contract.vendor.companyLegalName}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className={cn('px-3 py-1 text-xs font-medium rounded-full', STATUS_COLORS[statusConfig.color as keyof typeof STATUS_COLORS])}>
              {statusConfig.label}
            </span>
            <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
              {typeLabel}
            </span>
            {contract.isAutoRenewal && (
              <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 rounded-full flex items-center space-x-1">
                <ArrowPathIcon className="w-3 h-3" />
                <span>Auto-Renewal</span>
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {contract.contractValue !== null && contract.contractValue !== undefined && (
              <div className="flex items-center space-x-2">
                <CurrencyDollarIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                <span className="text-secondary-600 dark:text-secondary-400">Value:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  {vendorService.formatCurrency(contract.contractValue, contract.currency)}
                </span>
              </div>
            )}
            {(contract.effectiveDate ?? contract.expirationDate) && (
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                <span className="text-secondary-600 dark:text-secondary-400">Period:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  {vendorService.formatDate(contract.effectiveDate)} &rarr; {vendorService.formatDate(contract.expirationDate)}
                </span>
              </div>
            )}
            {contract.isAutoRenewal && contract.noticeDeadline && (
              <div className="flex items-center space-x-2">
                <ClockIcon className={cn('w-4 h-4', getNoticeDeadlineColor(contract.noticeDeadline))} />
                <span className="text-secondary-600 dark:text-secondary-400">Notice Deadline:</span>
                <span className={cn('font-medium', getNoticeDeadlineColor(contract.noticeDeadline))}>
                  {vendorService.formatDate(contract.noticeDeadline)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ContractsList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [autoRenewalOnly, setAutoRenewalOnly] = useState(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, _setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [totalContracts, setTotalContracts] = useState(0);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await vendorService.getContracts({
        page: currentPage,
        limit: 20,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        contractType: selectedType !== 'all' ? selectedType : undefined,
        isAutoRenewal: autoRenewalOnly ? true : undefined,
        search: searchQuery || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });

      setContracts(result.contracts);
      setTotalPages(result.pagination.totalPages);
      setTotalContracts(result.pagination.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch contracts:', err instanceof Error ? err.message : err);
      setError('Failed to load contracts');
      setContracts([]);
      setTotalContracts(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedType, autoRenewalOnly, searchQuery]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Compute stats from loaded contracts
  const stats = {
    total: totalContracts,
    active: contracts.filter(c => c.status === 'ACTIVE').length,
    draftOrReview: contracts.filter(c => c.status === 'DRAFT' || c.status === 'UNDER_REVIEW').length,
    expiringSoon: contracts.filter(c => c.status === 'EXPIRING_SOON').length,
  };

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Contract Management
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage and track vendor contracts across your organization
            </p>
          </div>
          <Link to="/legal/contracts/create">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="w-4 h-4 mr-2" />
              New Contract
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {stats.total}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Total Contracts
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                {stats.active}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Active
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {stats.draftOrReview}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Draft / Under Review
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {stats.expiringSoon}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Expiring Soon
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search contracts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="DRAFT">Draft</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="PENDING_APPROVAL">Pending Approval</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRING_SOON">Expiring Soon</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="TERMINATED">Terminated</option>
                </select>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Types</option>
                  <option value="MSA">Master Service Agreement</option>
                  <option value="SOW">Statement of Work</option>
                  <option value="NDA">Non-Disclosure Agreement</option>
                  <option value="SLA">Service Level Agreement</option>
                  <option value="SOFTWARE_LICENSE">Software License</option>
                  <option value="LEASE">Lease Agreement</option>
                  <option value="AMENDMENT">Amendment</option>
                  <option value="PURCHASE_ORDER">Purchase Order</option>
                  <option value="CONSULTING">Consulting Agreement</option>
                  <option value="OTHER">Other</option>
                </select>
                <label className="flex items-center space-x-2 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRenewalOnly}
                    onChange={(e) => setAutoRenewalOnly(e.target.checked)}
                    className="rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-900 dark:text-white whitespace-nowrap">Auto-Renewal</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Contracts List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading contracts...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Contracts
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                {error}
              </p>
              <Button onClick={fetchContracts} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : contracts.length > 0 ? (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <ContractCard key={contract.id} contract={contract} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <DocumentTextIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No contracts found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400">
                No contracts match your current filters. Try adjusting your search criteria.
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
